import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Format a UTC ISO datetime string (or date-only string) as a human-readable
 * date WITHOUT timezone offset errors.
 *
 * The backend stores dates as UTC datetimes, e.g. "2026-07-02T04:00:00Z".
 * Without `timeZone: 'UTC'`, `new Date()` converts to local time, which causes
 * US browsers to display the date as one day earlier (e.g. Jul 1 instead of Jul 2).
 *
 * Pass `opts` to override any Intl.DateTimeFormatOptions. Falls back to '—' for
 * null / undefined / empty values.
 */
export function formatDateUTC(
  iso: string | null | undefined,
  opts?: Intl.DateTimeFormatOptions
): string {
  if (!iso) return '—'
  const defaults: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC', // ← prevents local-timezone offset from shifting the date
  }
  return new Date(iso).toLocaleDateString('en-US', { ...defaults, ...opts })
}

/**
 * Extract the YYYY-MM-DD portion from a UTC ISO datetime string for use in
 * `<input type="date">` value props.
 *
 * Splitting on 'T' is correct here because the backend always stores dates at
 * midnight UTC (or an early-morning UTC time), so the date part of the ISO
 * string is the canonical calendar date we want to pre-fill.
 *
 * Returns '' for null / undefined / empty values.
 */
export function isoToDateInputValue(iso: string | null | undefined): string {
  if (!iso) return ''
  return iso.split('T')[0]
}

/**
 * Convert an `<input type="date">` value (YYYY-MM-DD) to a midnight UTC ISO
 * string suitable for sending to the backend.
 *
 * CRITICAL: Always append 'T00:00:00Z' (with the Z suffix), NOT 'T00:00:00'
 * (without Z). Without Z, `new Date()` interprets the string as LOCAL midnight,
 * which in timezones ahead of UTC (e.g. IST +5:30) would produce a UTC datetime
 * that is still on the PREVIOUS calendar day — causing the classic off-by-one
 * date bug (e.g. user picks Jul 14, backend receives Jul 13).
 *
 * Usage:
 *   dateInputToUTCIso('2026-07-14') → '2026-07-14T00:00:00.000Z'
 */
export function dateInputToUTCIso(dateValue: string): string {
  return new Date(dateValue + 'T00:00:00Z').toISOString()
}

/**
 * Whether `href` is safe to treat as an internal, same-origin app path.
 * Rejects anything with a URL scheme (`javascript:`, `data:`, etc.) and
 * protocol-relative paths (`//evil.com`, `/\evil.com`), which browsers
 * resolve as absolute, off-origin URLs despite starting with a slash.
 */
export function isSafeInternalHref(href: unknown): href is string {
  if (typeof href !== 'string' || href.length === 0) return false
  if (!href.startsWith('/')) return false
  if (href.startsWith('//') || href.startsWith('/\\')) return false
  return true
}

/**
 * Open an internal app path in a new tab, refusing anything that isn't a
 * same-origin relative path. These hrefs are backend/AI-generated (chat
 * suggestions, fuzzy-match results), so they're untrusted input reaching a
 * `window.open` XSS sink — a `javascript:`/`data:` URI in `href` would
 * otherwise execute in the current session.
 */
export function openInNewTab(href: unknown): void {
  if (!isSafeInternalHref(href)) return
  window.open(href, '_blank', 'noopener,noreferrer')
}
